import { tx as translateInterfaceText } from '@/utils/i18n';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { Layout } from '@/components/layout/Layout';
import { DashboardTopbar } from '@/components/dashboard/v3/DashboardTopbar';
import { VersionTag } from '@/components/layout/VersionTag';
import { DashboardVideoFeed } from '@/components/premium-dashboard/feed/DashboardVideoFeed';
import { VideoDragPreview } from '@/components/premium-dashboard/feed/VideoDragPreview';
import { VideoTablePremium } from '@/components/hierarchical-folders/VideoTablePremium';
import { FolderCard } from '@/components/hierarchical-folders/FolderCard';
import { FolderBreadcrumb } from '@/components/hierarchical-folders/FolderBreadcrumb';
import { CreateFolderModal } from '@/components/hierarchical-folders/CreateFolderModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Grid, List, Search, Loader2, Plus, Play as PlayIcon, Eye, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useVideosList } from '@/hooks/useVideosList';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { useHierarchicalFoldersStore } from '@/store/hierarchicalFoldersStore';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { videoService } from '@/services/videoService';
import { UploadQueueModal } from '@/components/upload/UploadQueueModal';
import { ExhaustedPlaysModal } from '@/components/modals/ExhaustedPlaysModal';
import { OfertaModal } from '@/components/modals/OfertaModal';
import { useTrialVideoQuota } from '@/hooks/useTrialVideoQuota';
import { NewFolderCard } from '@/components/dashboard/folders/NewFolderCard';
import { MetricsPanel } from '@/components/dashboard/metrics/MetricsPanel';
import { toast } from 'sonner';
import { t } from '@/utils/i18n';
import { normalizeText } from '@/utils/normalizeText';
import { useLanguage } from '@/hooks/useLanguage';

type ViewMode = 'grid' | 'list';
type SortMetric = 'plays' | 'views';
type SortOrder = 'desc' | 'asc';

interface SortOptionState {
  metric: SortMetric;
  order: SortOrder;
}

const VIEW_MODE_STORAGE_KEY = 'hostvsl:dashboard:viewMode';

export default function MyVideosV2() {
  useLanguage();
  const navigate = useNavigate();
  const { user } = useCustomAuth();

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return 'grid';
    return (localStorage.getItem(VIEW_MODE_STORAGE_KEY) as ViewMode) || 'grid';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<SortOptionState>({ metric: 'plays', order: 'desc' });
  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // Estado dos modais
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [ofertaOpen, setOfertaOpen] = useState(false);
  const [ofertaPlanId, setOfertaPlanId] = useState<'light' | 'plus' | 'premium' | undefined>();
  const [paywallOpen, setPaywallOpen] = useState(false);

  // Limite de vídeos do período de teste. O backend já recusa o envio; aqui só
  // antecipamos o aviso para o usuário não perder tempo subindo um arquivo que
  // será rejeitado.
  const { atingiuLimiteVslNormal, cupom, cupomCode } = useTrialVideoQuota();

  /**
   * Único caminho para abrir o envio de vídeos a partir do dashboard: quem já
   * gastou a cota do teste vê o paywall no lugar da fila de upload.
   */
  /**
   * Traduz a duração do cupom do Stripe para o texto do microcopy. Sem isto o
   * modal herdaria o "nos 2 primeiros meses" da primeira oferta e prometeria
   * mais desconto do que a cobrança vai respeitar — o cupom promocional atual,
   * por exemplo, é `once` (só o primeiro mês).
   */
  const descricaoDuracaoCupom = (() => {
    if (cupom?.duration === 'forever') return 'em todas as mensalidades';
    if (cupom?.duration === 'repeating') {
      const meses = cupom.duration_in_months ?? 0;
      if (meses > 1) return `nos ${meses} primeiros meses`;
      return translateInterfaceText("no primeiro mês");
    }
    // 'once' (ou desconhecido): assume o mínimo, nunca o mais generoso.
    return translateInterfaceText("no primeiro mês");
  })();

  const abrirEnvioDeVideos = () => {
    if (atingiuLimiteVslNormal) {
      setPaywallOpen(true);
      return;
    }
    setUploadModalOpen(true);
  };

  useEffect(() => {
    const trigger = sessionStorage.getItem('hs_show_oferta_after_signup');
    if (trigger !== '1') return;
    const userId = user?.id_user || user?.id;
    if (!userId) return;
    const localKey = `hs_oferta_shown_${userId}`;
    if (localStorage.getItem(localKey) === '1') {
      sessionStorage.removeItem('hs_show_oferta_after_signup');
      sessionStorage.removeItem('hs_purchased_plan_id');
      return;
    }
    const purchased = sessionStorage.getItem('hs_purchased_plan_id');
    if (purchased === 'light' || purchased === 'plus' || purchased === 'premium') {
      setOfertaPlanId(purchased);
    }
    setOfertaOpen(true);
    localStorage.setItem(localKey, '1');
    sessionStorage.removeItem('hs_show_oferta_after_signup');
    sessionStorage.removeItem('hs_purchased_plan_id');
  }, [user]);

  // Store de pastas hierárquicas
  const {
    rootFolders,
    currentFolder,
    isLoading: isFolderLoading,
    fetchRootFolders,
    fetchFolderDetails,
    renameFolder,
    deleteFolder,
    navigateToFolder,
    addVideosToFolder,
    removeVideosFromFolder,
  } = useHierarchicalFoldersStore();

  // Carregar pastas raiz ao montar
  useEffect(() => {
    fetchRootFolders();
  }, [fetchRootFolders]);

  // Listeners do tour
  useEffect(() => {
    const openUpload = () => setUploadModalOpen(true);
    const openCreateFolder = () => setCreateFolderOpen(true);
    const cleanup = () => {
      setUploadModalOpen(false);
      setCreateFolderOpen(false);
    };
    window.addEventListener('tour:open-upload', openUpload);
    window.addEventListener('tour:open-create-folder', openCreateFolder);
    window.addEventListener('tour:cleanup', cleanup);
    return () => {
      window.removeEventListener('tour:open-upload', openUpload);
      window.removeEventListener('tour:open-create-folder', openCreateFolder);
      window.removeEventListener('tour:cleanup', cleanup);
    };
  }, []);

  // Hook para buscar vídeos da API — declarado antes dos handlers de DnD
  // para que 'videos' esteja disponível no restante da página
  const {
    videos,
    isLoading,
    isFetching,
    updateSearch,
    updateSort,
    pagination,
    goToNextPage,
    goToPrevPage,
    prefetchNextPage,
    invalidateCache,
  } = useVideosList({
    initialLimit: 50,
    initialSortBy: 'plays',
    initialSortOrder: 'DESC',
  });

  // A API só ordena por 'createdAt' | 'title' | 'plays'. Com metric='plays'
  // a ordenação vai para o servidor (vale para todas as páginas); com 'views'
  // ela fica no cliente, via prop sortOption do feed (só a página carregada).
  useEffect(() => {
    if (sortOption.metric === 'plays') {
      updateSort('plays', sortOption.order === 'desc' ? 'DESC' : 'ASC');
    }
  }, [sortOption, updateSort]);

  const handleDeleteVideo = async (video: { id: string }) => {
    try {
      await videoService.deleteVideo(video.id);
      invalidateCache();
      toast.success(t('deleteVideoSuccess'));
    } catch (error) {
      toast.error(t('deleteVideoError'));
    }
  };

  const handleRenameVideo = (_videoId: string, _newTitle: string) => {
    invalidateCache();
  };

  useEffect(() => {
    updateSearch(debouncedSearch);
  }, [debouncedSearch, updateSearch]);

  useEffect(() => {
    if (viewMode === 'list' && videos.length > 0) {
      prefetchNextPage();
    }
  }, [viewMode, videos.length, prefetchNextPage]);

  useEffect(() => {
    const handleVideosUploaded = () => { invalidateCache(); };
    window.addEventListener('videos-uploaded', handleVideosUploaded);
    return () => window.removeEventListener('videos-uploaded', handleVideosUploaded);
  }, [invalidateCache]);

  // ── DnD ──────────────────────────────────────────────────────────────────

  // Estado do vídeo sendo arrastado (para o DragOverlay)
  const [activeDrag, setActiveDrag] = useState<{
    id: string;
    title: string;
    thumbnail?: string;
    views: number;
    plays: number;
  } | null>(null);

  // Configuração do sensor — exige arrastar ao menos 6px antes de ativar
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  // Início do drag: os dados vêm do próprio draggable (DraggableFeedVideoCard).
  // Ler daqui em vez de `videos.find(...)` é o que faz o ghost funcionar também
  // para vídeos que estão dentro de uma pasta (eles não estão em `videos`).
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current;
    if (data?.type !== 'video') return;
    setActiveDrag({
      id: data.videoId,
      title: data.videoTitle || translateInterfaceText("Vídeo"),
      thumbnail: data.videoThumbnail,
      views: data.videoViews || 0,
      plays: data.videoPlays || 0,
    });
  }, []);

  // Fim do drag: move vídeo para a pasta se solto sobre uma
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;
    if (active.data.current?.type !== 'video' || over.data.current?.type !== 'folder') return;
    const videoId: string = active.data.current?.videoId;
    const folderId: string = over.data.current?.folderId;
    if (!videoId || !folderId) return;
    const sourceFolderId = currentFolder?.id ?? null;
    try {
      await addVideosToFolder(folderId, [videoId]);
      if (sourceFolderId && sourceFolderId !== folderId) {
        await removeVideosFromFolder(sourceFolderId, [videoId]);
      }
      invalidateCache();
      toast.success(t('moveVideoSuccess'));
    } catch {
      toast.error(t('moveVideoError'));
    }
  }, [addVideosToFolder, removeVideosFromFolder, invalidateCache, currentFolder]);

  // Mover via menu de contexto ("Mover para")
  const handleMoveToFolder = useCallback(async (videoId: string, folderId: string) => {
    const sourceFolderId = currentFolder?.id ?? null;
    try {
      await addVideosToFolder(folderId, [videoId]);
      if (sourceFolderId && sourceFolderId !== folderId) {
        await removeVideosFromFolder(sourceFolderId, [videoId]);
      }
      invalidateCache();
      toast.success(t('moveVideoSuccess'));
    } catch {
      toast.error(t('moveVideoError'));
    }
  }, [addVideosToFolder, removeVideosFromFolder, invalidateCache, currentFolder]);

  // Remover vídeo da pasta atual
  const handleRemoveFromFolder = useCallback(async (videoId: string) => {
    if (!currentFolder) return;
    try {
      await removeVideosFromFolder(currentFolder.id, [videoId]);
      invalidateCache();
      toast.success(t('videoRemovedFromFolder'));
    } catch {
      toast.error(t('removeFromFolderError'));
    }
  }, [currentFolder, removeVideosFromFolder, invalidateCache]);

  // ── Navegação entre pastas ───────────────────────────────────────────────

  const handleFolderClick = (folderId: string) => {
    navigateToFolder(folderId);
  };

  const handleBreadcrumbNavigate = (folderId: string | null) => {
    if (folderId === null) {
      // Voltar para raiz
      useHierarchicalFoldersStore.setState({ currentFolder: null });
      fetchRootFolders();
    } else {
      navigateToFolder(folderId);
    }
  };

  // ── Ações de pasta ───────────────────────────────────────────────────────
  // O rename virou edição inline dentro do card (chega o NOVO nome) e o delete
  // já chega confirmado pelo DeleteFolderModal que vive dentro do card.

  const handleRenameFolder = useCallback(async (id: string, newName: string) => {
    try {
      await renameFolder(id, newName);
      toast.success(translateInterfaceText("Pasta renomeada"));
    } catch {
      toast.error(translateInterfaceText("Erro ao renomear pasta"));
    }
  }, [renameFolder]);

  const handleDeleteFolder = useCallback(async (id: string) => {
    try {
      await deleteFolder(id);
      if (currentFolder) {
        await fetchFolderDetails(currentFolder.id);
      } else {
        await fetchRootFolders();
      }
      toast.success(translateInterfaceText("Pasta excluída"));
    } catch {
      toast.error(translateInterfaceText("Erro ao excluir pasta"));
    }
  }, [deleteFolder, currentFolder, fetchFolderDetails, fetchRootFolders]);

  // ── Dados derivados ──────────────────────────────────────────────────────

  const isInsideFolder = !!currentFolder;

  // Pastas e vídeos a exibir dependendo do contexto
  const foldersToShow = useMemo(
    () => (currentFolder ? currentFolder.children : rootFolders),
    [currentFolder, rootFolders]
  );

  // Métricas por pasta agregadas pelo backend (/folders/root e /folders/:id)
  const folderStats = useMemo(
    () => new Map(foldersToShow.map(f => [f.id, { plays: f.totalPlays ?? 0, views: f.totalViews ?? 0 }])),
    [foldersToShow]
  );

  // Pastas ordenadas pelo mesmo critério escolhido para os vídeos
  const sortedFolders = useMemo(() => {
    return [...foldersToShow].sort((a, b) => {
      const valA = sortOption.metric === 'plays' ? (a.totalPlays ?? 0) : (a.totalViews ?? 0);
      const valB = sortOption.metric === 'plays' ? (b.totalPlays ?? 0) : (b.totalViews ?? 0);
      return sortOption.order === 'desc' ? valB - valA : valA - valB;
    });
  }, [foldersToShow, sortOption]);

  // Vídeos da pasta atual (mapeados para o formato do DashboardVideoFeed).
  // A busca da pasta é client-side: o conteúdo vem inteiro de /folders/:id, sem
  // passar pelo `search` da API. O feed em grade refiltra por conta própria; aqui
  // o filtro existe para o modo lista, que consome esta lista direto.
  const folderVideos = useMemo(() => {
    if (!currentFolder) return null;
    const term = normalizeText(debouncedSearch);
    const source = term
      ? currentFolder.videos.filter(v => normalizeText(v.title).includes(term))
      : currentFolder.videos;
    return source.map(v => ({
      id: v.id,
      title: v.title,
      thumbnailUrl: v.thumbnailUrl || null,
      duration: v.duration || 0,
      status: v.status || 'active',
      plays: v.plays || 0,
      views: v.views || 0,
      createdAt: v.createdAt,
      sharedId: v.sharedId || '',
      videoDownload: v.videoDownload || null,
    }));
  }, [currentFolder, debouncedSearch]);

  // Vídeos para modo lista (raiz). O filtro por acento roda também aqui porque a
  // busca da API é sensível a acentos: sem isso, "metricas" não acha "Métricas"
  // no modo lista (o modo grade já refiltra dentro do DashboardVideoFeed).
  const videosForTable = useMemo(() => {
    const term = normalizeText(debouncedSearch);
    const source = term
      ? videos.filter(video => normalizeText(video.title).includes(term))
      : videos;
    return source.map(video => ({
      id: video.id,
      title: video.title,
      thumbnailUrl: video.thumbnailUrl || '',
      plays: video.plays || 0,
      views: video.views || 0,
      createdAt: video.createdAt,
      updatedAt: video.updatedAt,
      duration: video.duration || 0,
      status: video.status,
      conversions: video.tracking?.conversions,
      revenue: video.tracking?.revenue,
      currency: video.tracking?.currency,
    }));
  }, [videos]);

  const videosLoading = isInsideFolder ? isFolderLoading : isLoading;
  const feedVideos = isInsideFolder ? folderVideos ?? [] : videos;
  const isEmptyFolder = isInsideFolder && !videosLoading && feedVideos.length === 0 && !debouncedSearch;

  // ── Blocos reutilizados na toolbar ───────────────────────────────────────

  const sortDropdown = (extraClass?: string) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={cn("flex items-center justify-between gap-1.5 h-[40px] px-2.5 lg:px-3 rounded-[10px] bg-white border border-[#E7ECF3] text-[13px] text-[#465468] hover:border-[#BCD0FA] transition-all outline-none cursor-pointer shadow-[0_1px_2px_rgba(16,24,40,.04)] focus-visible:ring-2 focus-visible:ring-[#BCD0FA] group max-w-full", extraClass)}>
          {/* Some abaixo de xl: entre 1024 e 1280px o espaço da linha não comporta
              este rótulo junto de "Visualizações" (bem mais largo que "Plays")
              sem empurrar a busca, que agora tem largura fixa. */}
          <span className="text-[12px] font-normal text-[#98A2B3] whitespace-nowrap hidden xl:inline">{translateInterfaceText("Ordenar por")}</span>
          <span className="font-medium whitespace-nowrap flex items-center gap-1.5">
            {sortOption.metric === 'plays' ? <PlayIcon className="h-3.5 w-3.5 text-[#3478F6]" /> : <Eye className="h-3.5 w-3.5 text-[#3478F6]" />}
            {sortOption.metric === 'plays' ? translateInterfaceText("Plays") : translateInterfaceText("Visualizações")}
          </span>
          <ChevronDown className="h-[14px] w-[14px] text-[#98A2B3] flex-shrink-0" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[230px] p-2 rounded-[12px] shadow-[0_4px_14px_rgba(16,24,40,.05)] border-[#E7ECF3] bg-white"
      >
        <div className="px-2 py-2 mb-1 text-[11px] font-bold text-[#98A2B3] uppercase tracking-wider">
          {translateInterfaceText("ORDENAR POR")}</div>

        <div className="group/list">
          {(['plays', 'views'] as const).map((metric) => {
            const isActive = sortOption.metric === metric;
            return (
              <DropdownMenuItem
                key={metric}
                onClick={(e) => {
                  e.preventDefault();
                  if (isActive) {
                    setSortOption(prev => ({ ...prev, order: prev.order === 'desc' ? 'asc' : 'desc' }));
                  } else {
                    setSortOption({ metric, order: 'desc' });
                  }
                }}
                className={cn(
                  "flex items-center justify-between px-3 py-2.5 mb-1 rounded-[8px] text-[13px] cursor-pointer transition-all duration-200 group/item",
                  isActive
                    ? "bg-[#2869E5] text-white font-medium shadow-[0_2px_8px_rgba(52,120,246,0.25)] group-hover/list:bg-white group-hover/list:text-[#465468] group-hover/list:shadow-none hover:!bg-[#2869E5] hover:!text-white focus:!bg-[#2869E5] data-[highlighted]:!bg-[#2869E5] data-[highlighted]:!text-white"
                    : "text-[#465468] hover:bg-[#2869E5] hover:text-white data-[highlighted]:!bg-[#2869E5] data-[highlighted]:!text-white"
                )}
              >
                <div className="flex items-center gap-2.5">
                  {metric === 'plays' ? (
                    <PlayIcon className={cn("h-3.5 w-3.5", isActive ? "text-white group-hover/list:text-[#465468] group-hover/item:!text-white" : "text-[#465468] group-hover/item:text-white")} />
                  ) : (
                    <Eye className={cn("h-3.5 w-3.5", isActive ? "text-white group-hover/list:text-[#465468] group-hover/item:!text-white" : "text-[#465468] group-hover/item:text-white")} />
                  )}
                  <span className={cn(isActive ? "text-white group-hover/list:text-[#465468] group-hover/item:!text-white" : "group-hover/item:text-white")}>
                    {metric === 'plays' ? translateInterfaceText("Plays") : translateInterfaceText("Visualizações")}
                  </span>
                </div>

                <div className="flex items-center gap-0.5">
                  <span className={cn(
                    "text-[10px] transition-all duration-200",
                    isActive && sortOption.order === 'asc' ? "text-white opacity-100 font-bold scale-110 group-hover/list:text-[#465468] group-hover/item:!text-white" : "opacity-30",
                    !isActive && "group-hover/item:text-white group-hover/item:opacity-40"
                  )}>▲</span>
                  <span className={cn(
                    "text-[10px] transition-all duration-200",
                    isActive && sortOption.order === 'desc' ? "text-white opacity-100 font-bold scale-110 group-hover/list:text-[#465468] group-hover/item:!text-white" : "opacity-30",
                    !isActive && "group-hover/item:text-white group-hover/item:opacity-40"
                  )}>▼</span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const addButton = (extraClass?: string, compact = false) => (
    <button
      data-tour="videos-add"
      onClick={abrirEnvioDeVideos}
      className={cn(
        // Sem overflow-hidden e sem shrink: o botão tem a largura do próprio
        // conteúdo, então ícone e rótulo nunca são cortados — quem cede espaço
        // é o dropdown de ordenação ao lado.
        "h-[40px] px-[20px] whitespace-nowrap rounded-[10px] bg-[#3478F6] border border-[#3478F6] text-white text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-[#2869E5] hover:border-[#2869E5] transition-all shadow-[0_1px_2px_rgba(16,24,40,.04)] hover:shadow-[0_4px_14px_rgba(16,24,40,.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#BCD0FA] cursor-pointer flex-shrink-0",
        extraClass
      )}
    >
      {!compact && (
        <span className="inline-flex items-center justify-center h-[22px] w-[22px] rounded-full bg-white flex-shrink-0">
          <Plus className="h-[13px] w-[13px] text-[#3478F6]" strokeWidth={2.6} />
        </span>
      )}
      {compact ? translateInterfaceText("Adicionar") : translateInterfaceText("Adicionar Vídeos")}
    </button>
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveDrag(null)}
    >
      <Layout>
        {/* h-screen no mobile (o <main> mobile não é flex) e flex-1 no desktop,
            onde o <main> já tem min-h-screen e fundo transparente. */}
        <div className="bg-[#F6F7F9] text-foreground w-full h-screen lg:h-auto lg:flex-1 lg:min-h-0 flex flex-col overflow-hidden">
          <DashboardTopbar />

          {/* Área de scroll */}
          <div className="flex-1 overflow-y-auto pb-6">

            {/* Breadcrumb — aparece quando está dentro de uma pasta */}
            {isInsideFolder && currentFolder && (
              <div className="px-6 pt-2 mb-4">
                <FolderBreadcrumb
                  breadcrumb={[...currentFolder.breadcrumb, { id: currentFolder.id, name: currentFolder.name, parentId: currentFolder.parentId, createdAt: currentFolder.createdAt, childrenCount: currentFolder.childrenCount, videosCount: currentFolder.videosCount }]}
                  onNavigate={handleBreadcrumbNavigate}
                />
              </div>
            )}

            {/* Painel de Métricas do Dia — apenas na raiz, fora do bloco branco */}
            {!isInsideFolder && (
              <div className="px-6 pt-2 mb-8">
                <MetricsPanel />
              </div>
            )}

            <div className="px-6 space-y-6">
              {/* ═══ Bloco único: Biblioteca ═══ */}
              <div className="rounded-[16px] bg-white p-6 shadow-[0_4px_14px_rgba(16,24,40,.05)] border border-[#D9E1EC]">

                {/* Seção 1: Pastas */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-[15px] font-semibold leading-tight text-[#667085]">
                      {isInsideFolder ? translateInterfaceText("Subpastas") : translateInterfaceText("Pastas")}
                    </h2>
                  </div>

                  {/* Grid fluido: a largura do card vem da coluna, não é fixa.
                      Alvo de 6 por linha em Full HD (a partir de 1800px), caindo
                      progressivamente até 1 no mobile — com 268px fixos, 6 cards
                      dariam 1668px numa área útil de ~1556px em 1920 e quebravam
                      para a linha de baixo. */}
                  <div className="grid gap-x-3 gap-y-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 min-[1800px]:grid-cols-6">
                    <NewFolderCard onClick={() => setCreateFolderOpen(true)} className="h-[72px] min-h-[72px]" />
                    {sortedFolders.map(folder => (
                      <FolderCard
                        key={folder.id}
                        folder={folder}
                        stats={folderStats.get(folder.id)}
                        metric={sortOption.metric}
                        onClick={() => handleFolderClick(folder.id)}
                        onRename={handleRenameFolder}
                        onDelete={handleDeleteFolder}
                      />
                    ))}
                  </div>
                </div>

                {/* Seção 2: Vídeos */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-[15px] font-semibold leading-tight text-[#667085]">
                        {isInsideFolder ? translateInterfaceText("Vídeos nesta pasta") : translateInterfaceText("Vídeos")}
                      </h2>
                      {!isInsideFolder && (
                        <p className="mt-1 text-[12px] leading-tight font-normal text-[#98A2B3]">
                          {translateInterfaceText("Gerencie, pesquise e organize sua biblioteca de vídeos")}</p>
                      )}
                    </div>
                  </div>

                  {/* ═══ Toolbar: Busca + Visualização + Adicionar ═══ */}
                  <div className="flex flex-wrap items-center gap-[10px] mb-6 w-full max-w-full lg:flex-nowrap lg:h-[40px]">

                    {/* Mobile/Tablet: ordenação + botão Adicionar na mesma linha */}
                    <div className="order-1 basis-full flex justify-end items-center gap-[8px] lg:hidden">
                      {viewMode === 'grid' && sortDropdown("w-auto")}
                      {addButton(undefined, true)}
                    </div>

                    {/* Seletor Visão Grade/Lista */}
                    <div className="order-2 lg:order-none inline-flex p-[3px] h-[40px] rounded-[10px] bg-[#F8FAFC] border border-[#E7ECF3] flex-shrink-0">
                      <button
                        aria-label={translateInterfaceText("Visão em grade")}
                        className={cn(
                          "flex items-center gap-2 h-[34px] px-[13px] rounded-[8px] text-[13px] font-medium transition-colors cursor-pointer border border-transparent",
                          viewMode === 'grid'
                            ? "bg-[#3478F6] text-white"
                            : "text-[#64748B] hover:text-[#3478F6] hover:bg-[#F3F7FF]"
                        )}
                        onClick={() => setViewMode('grid')}
                      >
                        <Grid className="h-[15px] w-[15px]" strokeWidth={1.7} />
                      </button>
                      <button
                        aria-label={translateInterfaceText("Visão em lista")}
                        className={cn(
                          "flex items-center gap-2 h-[34px] px-[13px] rounded-[8px] text-[13px] font-medium transition-colors cursor-pointer border border-transparent",
                          viewMode === 'list'
                            ? "bg-[#3478F6] text-white"
                            : "text-[#64748B] hover:text-[#3478F6] hover:bg-[#F3F7FF]"
                        )}
                        onClick={() => setViewMode('list')}
                      >
                        <List className="h-[15px] w-[15px]" strokeWidth={1.7} />
                      </button>
                    </div>

                    {/* Busca — no desktop tem largura FIXA, alinhada com a borda
                        direita do primeiro card do feed: o grid é lg:grid-cols-4
                        com gap-x-[18px], então uma coluna vale
                        calc((100% - 54px)/4); descontam-se ~100px do seletor
                        Grade/Lista + gap que vêm antes dela na mesma linha.
                        O lg:flex-none é obrigatório: sem ele o flex-1 do mobile
                        continua valendo e a barra estica pela linha toda. */}
                    <div className="order-3 lg:order-none relative flex-1 min-w-0 max-w-full lg:flex-none lg:w-[calc((100%-54px)/4-100px)] lg:min-w-[150px]">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[15px] w-[15px] text-[#98A2B3] pointer-events-none" strokeWidth={1.8} />
                      <input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={translateInterfaceText("Buscar vídeos...")}
                        aria-label={translateInterfaceText("Buscar vídeos")}
                        className="w-full h-[40px] pl-10 pr-3.5 rounded-[10px] bg-white border border-[#E7ECF3] text-[13px] text-[#465468] placeholder:text-[#98A2B3] outline-none focus:border-[#BCD0FA] focus:ring-2 focus:ring-[#BCD0FA]/40 transition-colors shadow-[0_1px_2px_rgba(16,24,40,.04)]"
                      />
                    </div>

                    {/* Desktop: Ordenação + Adicionar.
                        Ambos com largura de conteúdo (shrink-0): com basis em %
                        o botão cortava o rótulo e o dropdown vazava o texto, já
                        que o conteúdo dos dois é whitespace-nowrap. */}
                    <div className="hidden lg:flex items-center gap-[8px] ml-auto flex-shrink-0">
                      {viewMode === 'grid' && sortDropdown("flex-shrink-0")}
                      {addButton("pl-[12px] pr-[18px]")}
                    </div>

                  </div>

                  {/* Conteúdo: Vídeos em Grade */}
                  {viewMode === 'grid' && (
                    isEmptyFolder ? (
                      <div className="flex flex-col items-center justify-center py-14 px-6 rounded-[12px] border border-dashed border-[#BCD0FA] bg-[#FBFCFE] text-center">
                        <p className="text-[15px] font-semibold text-[#101828]">{translateInterfaceText("Nenhum vídeo nesta pasta")}</p>
                        <p className="mt-1 text-[12.5px] text-[#98A2B3]">
                          {translateInterfaceText("Arraste vídeos da biblioteca para organizá-los aqui.")}</p>
                      </div>
                    ) : (
                      <DashboardVideoFeed
                        searchQuery={debouncedSearch}
                        sortOption={sortOption}
                        videos={feedVideos}
                        isLoading={videosLoading}
                        folders={rootFolders}
                        onMoveToFolder={handleMoveToFolder}
                        isInsideFolder={isInsideFolder}
                        onRemoveFromFolder={handleRemoveFromFolder}
                      />
                    )
                  )}

                  {/* Conteúdo: Vídeos em Lista */}
                  {viewMode === 'list' && (
                    <>
                      {videosLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="h-8 w-8 animate-spin text-[#3478F6]" />
                        </div>
                      ) : feedVideos.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-14 px-6 rounded-[12px] border border-dashed border-[#BCD0FA] bg-[#FBFCFE] text-center">
                          <p className="text-[15px] font-semibold text-[#101828]">
                            {isInsideFolder ? translateInterfaceText("Nenhum vídeo nesta pasta") : translateInterfaceText("Nenhum vídeo encontrado")}
                          </p>
                          {searchQuery && (
                            <Button variant="link" onClick={() => setSearchQuery('')} className="mt-2">
                              {translateInterfaceText("Limpar busca")}</Button>
                          )}
                        </div>
                      ) : (
                        <>
                          <VideoTablePremium
                            videos={isInsideFolder
                              ? (folderVideos ?? []).map(v => ({ ...v, thumbnailUrl: v.thumbnailUrl || '' }))
                              : videosForTable
                            }
                            sortOption={sortOption}
                            onVideoClick={(video) => navigate(`/personalizations/${video.id}`)}
                            onVideoDelete={handleDeleteVideo}
                            onVideoRename={handleRenameVideo}
                          />

                          {/* Paginação — apenas na raiz */}
                          {!isInsideFolder && pagination && pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between mt-6 px-4">
                              <p className="text-sm text-muted-foreground">
                                {translateInterfaceText("Página")}{" "}{pagination.page} {" "}{translateInterfaceText("de")}{" "}{pagination.totalPages} ({pagination.total} {" "}{translateInterfaceText("vídeos)")}</p>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={goToPrevPage}
                                  disabled={!pagination.hasPrevPage || isFetching}
                                >
                                  {translateInterfaceText("Anterior")}</Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={goToNextPage}
                                  disabled={!pagination.hasNextPage || isFetching}
                                >
                                  {translateInterfaceText("Próxima")}</Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>

      <VersionTag />

      {/* FAB mobile */}
      <button
        onClick={abrirEnvioDeVideos}
        className="sm:hidden fixed bottom-[18px] right-[18px] z-50 w-[54px] h-[54px] rounded-full bg-primary text-primary-foreground shadow-[0_6px_16px_hsl(var(--primary)/0.35)] flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all duration-200"
        aria-label={translateInterfaceText("Adicionar vídeos")}
      >
        <Plus className="w-[22px] h-[22px]" />
      </button>

      {/* Modais ficam fora do <Layout>: ele renderiza {children} duas vezes
          (bloco mobile + bloco desktop) e portais apareceriam em dobro. */}
      <UploadQueueModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
      />

      <CreateFolderModal
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        parentId={currentFolder?.id ?? null}
        onSuccess={() => {
          setCreateFolderOpen(false);
          if (currentFolder) {
            fetchFolderDetails(currentFolder.id);
          } else {
            fetchRootFolders();
          }
        }}
      />

      <ExhaustedPlaysModal />
      <OfertaModal open={ofertaOpen} onOpenChange={setOfertaOpen} purchasedPlanId={ofertaPlanId} />

      {/* Paywall do período de teste. Mesmo componente da primeira oferta, com
          outro contexto: sem cronômetro (ele desabilita os CTAs ao zerar, o que
          num paywall significa impedir o usuário de pagar) e sem inventar
          desconto — quem veio por cupom mantém o desconto que já era dele.

          O percentual exibido vem do Stripe (POST /cupom), não de constante
          local. Cupom de valor fixo (amount_off) não vira fração aplicável a
          todos os planos, então nesse caso mostramos o preço cheio e deixamos o
          Stripe abater no fechamento — o usuário paga menos do que viu, nunca
          mais. */}
      <OfertaModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        discount={cupom?.percent_off ? cupom.percent_off / 100 : 0}
        discountDurationLabel={descricaoDuracaoCupom}
        coupon={cupomCode ?? ''}
        showCountdown={false}
        srTitle={translateInterfaceText("Ative seu plano para enviar mais vídeos")}
        badgeText={
          cupom?.percent_off
            ? translateInterfaceText("Seu desconto de {percent}% continua valendo", { percent: Math.round(cupom.percent_off) })
            : translateInterfaceText("Ative seu plano para continuar")
        }
        description={translateInterfaceText("Você atingiu o limite de vídeos do período de teste. Ative seu plano agora para voltar a enviar vídeos — leva menos de um minuto.")}
        safetyText={translateInterfaceText("O vídeo que você já subiu, as configurações e as métricas continuam salvos. Ativar o plano apenas libera novos envios.")}
        footerNote={
          cupomCode
            ? translateInterfaceText("O cupom aplicado na sua assinatura é mantido na ativação.")
            : null
        }
      />

      {/* DragOverlay — renderizado fora da árvore React, movido pelo browser direto (60fps suave) */}
      <DragOverlay modifiers={[restrictToWindowEdges]} dropAnimation={{ duration: 200, easing: 'ease' }}>
        {activeDrag ? (
          <VideoDragPreview
            title={activeDrag.title}
            thumbnail={activeDrag.thumbnail}
            views={activeDrag.views}
            plays={activeDrag.plays}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}